import * as Yup from "yup";
import { DOBDateInputSchema } from "~lib/helpers/DOBDateInputSchema";
import {
  NamingFieldType,
  SignupFormFieldFragmentFragment,
} from "~graphql/typed-document-nodes";

type ValidationSchemaProps = {
  signupFormFields?: SignupFormFieldFragmentFragment[];
};

export const initialFormValues = (
  signupFormFields: SignupFormFieldFragmentFragment[]
): Record<string, string | boolean | { value: string; label: string }> => {
  return signupFormFields.reduce<
    Record<string, string | boolean | { value: string; label: string }>
  >((acc, { type, name, options }) => {
    if (
      !name &&
      type !== NamingFieldType.UsableForSmsMarketing &&
      type !== NamingFieldType.UsableForEmailMarketing
    ) {
      return acc; // Skip if name is undefined (except for known types)
    }

    switch (type) {
      case NamingFieldType.UsableForSmsMarketing:
        acc[NamingFieldType.UsableForSmsMarketing] = true;
        break;
      case NamingFieldType.UsableForEmailMarketing:
        acc[NamingFieldType.UsableForEmailMarketing] = true;
        break;
      case NamingFieldType.CustomSelect:
        // Ensure value is assigned as an object with both 'value' and 'label'
        acc[name] = options[0]
          ? { value: options[0], label: options[0] }
          : { value: "", label: "" };
        break;
      case NamingFieldType.CustomCheckbox:
        acc[name] = false;
        break;
      case NamingFieldType.CustomText:
        acc[name] = "";
        break;
      default:
        acc[type] = "";
    }

    return acc;
  }, {});
};

export const validationSchema = ({
  signupFormFields = [],
}: ValidationSchemaProps) => {
  const birthDateField = signupFormFields?.find(
    ({ type }) => type === "BIRTH_DATE"
  );

  const validationShape = {
    [NamingFieldType.FirstName]: signupFormFields?.find(
      ({ type }) => type === NamingFieldType.FirstName
    )?.required
      ? Yup.string().required()
      : Yup.string().nullable(),
    [NamingFieldType.LastName]: signupFormFields?.find(
      ({ type }) => type === NamingFieldType.LastName
    )?.required
      ? Yup.string().required()
      : Yup.string().nullable(),
    [NamingFieldType.CompanyName]: signupFormFields?.find(
      ({ type }) => type === NamingFieldType.CompanyName
    )?.required
      ? Yup.string().required()
      : Yup.string().nullable(),
    [NamingFieldType.Email]: Yup.string().email().required(),
    [NamingFieldType.Gender]: signupFormFields?.find(
      ({ type }) => type === NamingFieldType.Gender
    )?.required
      ? Yup.string().required()
      : Yup.string().nullable(),
    [NamingFieldType.PhoneNumber]: signupFormFields?.find(
      ({ type }) => type === NamingFieldType.PhoneNumber
    )?.required
      ? Yup.string()
          .matches(/^\+?\d+$/, "Invalid phone number")
          .required()
      : Yup.string()
          .matches(/^\+?\d+$/, "Invalid phone number")
          .nullable(),
    [NamingFieldType.BirthDate]: DOBDateInputSchema.test({
      name: "dob-date-test",
      test(value) {
        if (birthDateField?.required && !value) {
          return this.createError({ message: "Date of birth is required" });
        }
        return true;
      },
    }),
    [NamingFieldType.PostalCode]: signupFormFields?.find(
      ({ type }) => type === NamingFieldType.PostalCode
    )?.required
      ? Yup.string().required()
      : Yup.string().nullable(),
    [NamingFieldType.UsableForEmailMarketing]: signupFormFields?.find(
      ({ type }) => type === NamingFieldType.UsableForEmailMarketing
    )?.required
      ? Yup.bool().oneOf([true], "This field is required")
      : Yup.boolean().nullable(),
    [NamingFieldType.UsableForSmsMarketing]: signupFormFields?.find(
      ({ type }) => type === NamingFieldType.UsableForSmsMarketing
    )?.required
      ? Yup.bool().oneOf([true], "This field is required")
      : Yup.boolean().nullable(),
  };

  signupFormFields.forEach(({ type, name, required }) => {
    if (
      ![
        NamingFieldType.CustomSelect,
        NamingFieldType.CustomText,
        NamingFieldType.CustomCheckbox,
      ].includes(type)
    )
      return;

    validationShape[name] =
      type === NamingFieldType.CustomSelect
        ? required
          ? Yup.string().required()
          : Yup.string().nullable()
        : type === NamingFieldType.CustomCheckbox
        ? required
          ? Yup.bool().oneOf([true], "This field is required")
          : Yup.bool().nullable()
        : required
        ? Yup.string().required()
        : Yup.string().nullable();
  });

  return Yup.object().shape(validationShape);
};
